// UK qualifying earnings thresholds by tax year (April to April)
// Source: https://www.gov.uk/workplace-pensions/what-your-employer-must-do
const THRESHOLDS_BY_YEAR = {
  '2024': { lower: 6240, upper: 50270 },   // 2024/25
  '2025': { lower: 6240, upper: 50270 },   // 2025/26
  '2026': { lower: 6240, upper: 50270 },   // 2026/27
};

// Returns the threshold for the current tax year (April start)
function getThresholds() {
  const now = new Date();
  // Tax year starts in April: before April = previous calendar year's thresholds
  const taxYear = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
  const key = String(taxYear);
  // Fall back to the most recent year we have
  if (THRESHOLDS_BY_YEAR[key]) return THRESHOLDS_BY_YEAR[key];
  const years = Object.keys(THRESHOLDS_BY_YEAR).sort();
  return THRESHOLDS_BY_YEAR[years[years.length - 1]];
}

export function calculatePensionContribution(type, value, monthlyGross, earningsBasis) {
  const parsed = parseFloat(value) || 0;
  if (parsed < 0) return -1; // Signal invalid value

  if (type === 'fixed-amount') {
    return parsed;
  }

  // Percentage-based
  let pensionableEarnings;

  if (earningsBasis === 'qualifying') {
    const { lower, upper } = getThresholds();
    const monthlyLower = lower / 12;
    const monthlyUpper = upper / 12;
    pensionableEarnings = Math.max(0, Math.min(monthlyGross, monthlyUpper) - monthlyLower);
  } else {
    pensionableEarnings = monthlyGross;
  }

  return pensionableEarnings * (parsed / 100);
}

// Basic-rate tax relief added "at source" under a Relief at Source scheme.
// HMRC always tops up 20% into the pot regardless of the member's marginal
// rate — any higher-rate relief is claimed separately via self-assessment and
// never touches payroll. So the employee only ever pays (1 − 0.20) of the
// gross contribution from their net pay.
export const RAS_BASIC_RATE_RELIEF = 0.20;

// Split a gross employee contribution across the payslip according to the
// scheme type. For Relief at Source the employee pays 80% from net pay and
// HMRC adds the remaining 20% into the pot. For salary sacrifice / net pay the
// full gross comes off pay (pre-tax), so from-pay === gross.
// `grossAmount` may be negative (the calc layer signals invalid input with -1)
// — in that case we pass it through untouched so callers' validity checks work.
export function employeeContributionBreakdown(grossAmount, schemeType) {
  if (schemeType === 'relief_at_source' && grossAmount >= 0) {
    const fromPay = grossAmount * (1 - RAS_BASIC_RATE_RELIEF);
    return { fromPay, taxRelief: grossAmount - fromPay, pot: grossAmount, reliefAtSource: true };
  }
  return { fromPay: grossAmount, taxRelief: 0, pot: grossAmount, reliefAtSource: false };
}

// ---------------------------------------------------------------------------
// Part-time working pattern
//
// Mirrors partTimeFraction() in backend/src/utils/salaryHistory.js — keep the two in
// step. The pattern (pt_weekly_hours / pt_fte_weekly_hours, v40) is the source of truth;
// the prorata_salary / annual_salary ratio is a fallback for rows whose pattern could
// not be derived, and returns exactly what this code did before v40.
//
// Gating on employment_type rather than contract_type matters: contract_type conflates
// contract duration with working pattern, and the new-starter form writes 'permanent'
// there — so part-timers created that way read as full-time.
export function isPartTime(employee) {
  if (!employee) return false;
  return employee.employment_type
    ? employee.employment_type === 'part-time'
    : employee.contract_type === 'part-time';
}

export function partTimeFraction(employee) {
  if (!isPartTime(employee)) return 1;

  const hours = parseFloat(employee.pt_weekly_hours) || 0;
  const fteHours = parseFloat(employee.pt_fte_weekly_hours) || 0;
  if (hours > 0 && fteHours > 0) {
    const fraction = hours / fteHours;
    return fraction > 0 && fraction < 1 ? fraction : 1;
  }

  const proRata = parseFloat(employee.prorata_salary) || 0;
  const fte = parseFloat(employee.annual_salary) || 0;
  if (proRata <= 0 || fte <= 0) return 1;
  const fraction = proRata / fte;
  return fraction > 0 && fraction < 1 ? fraction : 1;
}

// Human-readable pattern, e.g. "30 of 37.5 hrs (80% of full-time)". Returns null for
// full-time staff and where no pattern is recorded.
export function partTimePatternLabel(employee) {
  const fraction = partTimeFraction(employee);
  if (fraction >= 1) return null;
  const pct = `${Math.round(fraction * 1000) / 10}% of full-time`;
  const hours = parseFloat(employee.pt_weekly_hours) || 0;
  const fteHours = parseFloat(employee.pt_fte_weekly_hours) || 0;
  return hours > 0 && fteHours > 0 ? `${hours} of ${fteHours} hrs (${pct})` : pct;
}

// The monthly salary an employee actually receives in a period.
// IMPORTANT: for part-time staff this is the pro-rata figure (prorata_salary),
// NOT the full-time-equivalent (annual_salary). Hourly staff are rate × hours
// worked this period. Prefers the period-effective salary from salary history
// (effective_annual_salary) when the caller has it joined in.
export function effectiveMonthlySalary(employee) {
  if (!employee) return 0;
  if (employee.pay_type === 'hourly') {
    const rate = parseFloat(employee.hourly_rate) || 0;
    const hours = parseFloat(employee.hours_this_period) || 0;
    return rate * hours;
  }
  const effectiveAnnual = employee.effective_annual_salary !== undefined && employee.effective_annual_salary !== null
    ? parseFloat(employee.effective_annual_salary)
    : (parseFloat(employee.annual_salary) || 0);
  // Scale the period-effective FTE by the working pattern. Reading prorata_salary
  // directly would ignore a salary change that had not yet been written to that
  // derived column, and would miss part-timers whose contract_type says 'permanent'.
  return (effectiveAnnual / 12) * partTimeFraction(employee);
}

// Monthly gross used as the base for pension contribution calcs:
// the effective (pro-rata-aware) monthly salary plus this period's commission.
export function monthlyGrossForPension(employee) {
  return effectiveMonthlySalary(employee) + (parseFloat(employee.commission) || 0);
}

export function formatCurrency(amount, { decimals = 2 } = {}) {
  const value = Number(amount) || 0;
  // 'auto' shows pennies only when the amount actually has them — whole figures
  // stay clean (£30,000) while fractional ones aren't silently rounded
  // (£6,979.19 must not display as £6,979).
  const places = decimals === 'auto'
    ? (Number.isInteger(value) ? 0 : 2)
    : decimals;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: places,
    maximumFractionDigits: places,
  }).format(value);
}
